import React, { useReducer, useState } from 'react';
import { DevTool } from '@hookform/devtools';
import { ChangePasswordFieldNames, useChangePasswordForm } from 'hooks/forms';
import { useChangePassword } from 'hooks/api/changePassword';
import { passwordVisibilityInitialData, passwordVisibilityReducer } from 'hooks/reducers';

import { useModalState } from 'hooks/modals';
import Field from 'components/Field';
import Button from 'components/Button';
import SecureIcon from 'components/SecureIcon';
import PasswordRulesModal from 'components/PasswordRulesModal';
import styles from './ChangePassword.module.css';
import messages from './ChangePassword.messages';
import VerifyChangingPasswordModal from './ChangePasswordVerify/VerifyChangingPasswordModal';

export default function ChangePassword() {
  const {
    form,
    customState: { focusedField, isCompleted, onBlur, onFocus },
  } = useChangePasswordForm();
  const { mutateAsync: changePassword, isLoading } = useChangePassword();
  const [phoneLastFourDigits, setPhoneLastFourDigits] = useState('');

  const { isModalVisible, openModal, closeModal } = useModalState();

  const [passwordHidden, setPasswordHiding] = useReducer(
    passwordVisibilityReducer,
    passwordVisibilityInitialData,
  );

  const onSubmit = () => {
    const { currentPassword, newPassword } = form.getValues();

    changePassword({ currentPassword, newPassword }).then((code) => {
      setPhoneLastFourDigits(code.phoneLastFourDigits);
      openModal();
    });
  };

  const resendCode = async () => {
    const { currentPassword, newPassword } = form.getValues();
    await changePassword({ currentPassword, newPassword });
  };

  return (
    <div>
      <h1 className={styles.changePassword__title}>{messages.title}</h1>
      <p className={styles.changePassword__subtitle}>{messages.subtitle}</p>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DevTool control={form.control} />
        <Field
          form={form}
          name={ChangePasswordFieldNames.currentPassword}
          placeholder={messages.placeholder.currentPassword}
          className={styles.input}
          defaultValue={''}
          onFocus={() => onFocus(ChangePasswordFieldNames.currentPassword)}
          type={passwordHidden?.currentPassword ? 'password' : 'text'}
          icon={
            <SecureIcon
              changeSecuredState={() => {
                setPasswordHiding(ChangePasswordFieldNames.currentPassword);
              }}
            />
          }
        />
        <div className={styles.changePassword__modalWrapper}>
          <Field
            form={form}
            name={ChangePasswordFieldNames.newPassword}
            placeholder={messages.placeholder.newPassword}
            className={styles.input}
            defaultValue={''}
            onFocus={() => onFocus(ChangePasswordFieldNames.newPassword)}
            onBlur={onBlur}
            type={passwordHidden?.newPassword ? 'password' : 'text'}
            icon={
              <SecureIcon
                changeSecuredState={() => {
                  setPasswordHiding(ChangePasswordFieldNames.newPassword);
                }}
              />
            }
          />
          <PasswordRulesModal
            name={ChangePasswordFieldNames.newPassword}
            control={form.control}
            isShow={focusedField === ChangePasswordFieldNames.newPassword}
          />
        </div>
        <div className={styles.changePassword__modalWrapper}>
          <Field
            form={form}
            name={ChangePasswordFieldNames.repeatNewPassword}
            placeholder={messages.placeholder.repeatNewPassword}
            onFocus={() => onFocus(ChangePasswordFieldNames.repeatNewPassword)}
            onBlur={onBlur}
            className={styles.input}
            defaultValue={''}
            type={passwordHidden?.repeatNewPassword ? 'password' : 'text'}
            icon={
              <SecureIcon
                changeSecuredState={() => {
                  setPasswordHiding(ChangePasswordFieldNames.repeatNewPassword);
                }}
              />
            }
          />
          <PasswordRulesModal
            name={ChangePasswordFieldNames.repeatNewPassword}
            control={form.control}
            isShow={focusedField === ChangePasswordFieldNames.repeatNewPassword}
          />
        </div>

        <Button
          type={'submit'}
          displayDisabledUI={!isCompleted}
          isLoading={isLoading}
          className={styles.changePassword__button}
        >
          {messages.submitButton}
        </Button>
      </form>
      <VerifyChangingPasswordModal
        onRequestClose={closeModal}
        onResendCode={resendCode}
        newPassword={form.getValues().newPassword}
        phoneLastFourDigits={phoneLastFourDigits}
        isOpen={isModalVisible}
      />
    </div>
  );
}
